import { useCallback } from "react";
import { ParamListBase } from "@react-navigation/native";
import { RootStackParamList } from "../navigations/RootNavigation";
import { useCurrentProfile } from "./use-current-profile";
import { SYSTEM_NAME } from "../utils";

type RouteUnion<P extends ParamListBase> = {
    [K in keyof P]: { name: K; params: P[K] };
}[keyof P];

export function useActivityNavigationInfo<T extends ParamListBase = RootStackParamList>() {
    const currentProfile = useCurrentProfile();
    const callback = useCallback(
        (
            activityName: string,
            activityKey?: string | null,
        ): RouteUnion<RootStackParamList> | null => {
            if (activityName == "InvitationNeedResponse") {
                return {
                    name: "Invitation",
                    params: {
                        invitationId: activityKey as string,
                    },
                } as const;
            }
            if (
                activityName == "ContractWorkerNeedSign" ||
                activityName == "ContractClientNeedSign" ||
                activityName == "ContractContinueSigning"
            ) {
                return {
                    name: "MissionDetails",
                    params: { missionId: activityKey as string },
                } as const;
            }
            if (activityName == "NeedKyc") {
                return {
                    name: "PreIdVerification",
                    params: { backOnSkip: true },
                } as const;
            }
            if (activityName == "VerifyEmail") {
                return {
                    name: "ResendEmailVerification",
                    params: { clientProfile: false },
                } as const;
            }
            if (activityName == "VerifyBusinessEmail") {
                return {
                    name: "ResendEmailVerification",
                    params: { clientProfile: true },
                } as const;
            }
            if (activityName == "WorkerNeedCompletion") {
                return {
                    name: "Home",
                    params: { screen: "ProfileStack" },
                } as const;
            }
            if (activityName == "TimeSheetNeedCompletion") {
                return {
                    name: "TimesheetWorkerInput",
                    params: { id: activityKey as string },
                } as const;
            }
            if (activityName == "TimeSheetNeedChanges") {
                return {
                    name: "TimesheetWorkerInput",
                    params: { id: activityKey as string },
                } as const;
            }
            if (activityName == "TimeSheetNeedReview") {
                return {
                    name: "TimesheetClientReview",
                    params: { id: activityKey as string },
                } as const;
            }
            if (activityName === "EmailVerified") {
                return null;
            }
            if (activityName === "InvitationSelfAccepted") {
                return null;
            }
            if (activityName === "InvitationOtherAccepted") {
                return null;
            }
            if (activityName === "InvitationSelfRejected") {
                return null;
            }
            if (activityName === "InvitationOtherRejected") {
                return null;
            }
            if (activityName === "SalaryPaid") {
                return {
                    name: "SalaryDetails",
                    params: {
                        salaryId: activityKey as string,
                    },
                } as const;
            }
            if (activityName === "NewInvoiceAvailable") {
                return {
                    name: "InvoiceDetails",
                    params: {
                        invoiceId: activityKey as string,
                    },
                } as const;
            }
            if (
                activityName.startsWith("ContractSignedFor") ||
                activityName.startsWith("ContractAcceptedFor") ||
                activityName.startsWith("ContractCanceledBy") ||
                activityName.startsWith("ContractTerminatedBy") ||
                activityName.startsWith("ContractEndedFor")
            ) {
                return {
                    name: "MissionDetails",
                    params: { missionId: activityKey as string },
                } as const;
            }
            if (activityName === "TimeSheetAccepted") {
                return {
                    name: "TimesheetWorkerReview",
                    params: { id: activityKey as string },
                } as const;
            }
            if (activityName === "PayrollBreakdownNeedCompletion") {
                return {
                    name: "PayrollBreakdownEditor",
                    params: { payrollBreakdownId: activityKey as string },
                } as const;
            }
            if (
                activityName === "AmendmentWorkerNeedSign" ||
                activityName === "AmendmentClientNeedSign"
            ) {
                return {
                    name: "ContractPreviewScreen",
                    params: { contractId: activityKey as string, contractType: "amendment" },
                } as const;
            }
            if (
                activityName === "ClientContractNeedSign" &&
                currentProfile?.client?.contractEffectiveAsset &&
                currentProfile?.client?.signatureStatus
            ) {
                return {
                    name: "ContractPreviewScreen",
                    params: {
                        contractId: activityKey as string,
                        contractType: "client_framework",
                    },
                } as const;
            }
            return null;
        },
        [currentProfile?.client?.contractEffectiveAsset, currentProfile?.client?.signatureStatus],
    );
    return callback as (activityName: string, activityKey?: string | null) => RouteUnion<T> | null;
}

export type ActivityFilterType =
    | "breakdown"
    | "invoice"
    | "contract"
    | "account"
    | "timesheet"
    | "other";

/**
 * Return the activity filter type for a given activity name
 * @param activityName
 */
export const getActivityFilterType = (activityName: string): ActivityFilterType => {
    if (
        activityName == "ContractWorkerNeedSign" ||
        activityName == "ContractClientNeedSign" ||
        activityName == "ContractContinueSigning" ||
        activityName == "AmendmentWorkerNeedSign" ||
        activityName == "AmendmentClientNeedSign" ||
        activityName == "ClientContractNeedSign" ||
        activityName.startsWith("ContractSignedFor") ||
        activityName.startsWith("ContractAcceptedFor") ||
        activityName.startsWith("ContractCanceledBy") ||
        activityName.startsWith("ContractTerminatedBy") ||
        activityName.startsWith("ContractEndedFor")
    ) {
        return "contract";
    }
    if (activityName === "NewInvoiceAvailable") {
        return "invoice";
    }

    if (activityName === "PayrollBreakdownNeedCompletion") {
        return "breakdown";
    }

    if (
        activityName == "NeedKyc" ||
        activityName == "VerifyBusinessEmail" ||
        activityName == "VerifyEmail" ||
        activityName == "WorkerNeedCompletion"
    ) {
        return "account";
    }

    if (activityName == "TimeSheetNeedCompletion" || activityName === "TimeSheetNeedChanges") {
        return "timesheet";
    }

    return "other";
};

export const getActivityTitle = (title: string) => {
    if (title === "__APP_NAME__") {
        return SYSTEM_NAME;
    }
    return title;
};
